import React, { useState } from "react";
import {
  FileText,
  Users,
  Settings,
  ArrowRight,
  CheckCircle,
  Clock,
  Bell,
  Copy,
  MousePointer,
  UserCircle,
  PenTool,
  Type,
  Calendar,
  CheckSquare,
} from "lucide-react";
import SignatureCanvas from "../SignatureCanvas";
import SignatureTyped from "../SignatureTyped";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Signer {
  name: string;
  email: string;
  role: string;
  order: number;
  clientId?: string;
  color: string;
}

interface DocumentEntry {
  documentId: string;
  title: string;
  requiresSignature: boolean;
}

interface Field {
  documentIndex: number;
  signerIndex: number;
  type: "signature" | "initials" | "date_signed" | "text" | "checkbox";
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string;
}

interface WizardData {
  matterId: string;
  templateId: string;
  subject: string;
  message: string;
  documents: DocumentEntry[];
  signers: Signer[];
  fields: Field[];
  sequentialSigning: boolean;
  expirationDays: number;
  reminders: { enabled: boolean; intervalDays: number; maxReminders: number };
  saveAsTemplate: boolean;
  templateName: string;
  senderFieldValues?: Record<number, string>;
}

interface ReviewStepProps {
  data: WizardData;
  onChange?: (updated: WizardData) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getFieldSummary(fields: Field[], signers: Signer[]) {
  const perSigner: Record<number, Record<string, number>> = {};

  for (const field of fields) {
    if (!perSigner[field.signerIndex]) {
      perSigner[field.signerIndex] = {};
    }
    const typeCount = perSigner[field.signerIndex];
    typeCount[field.type] = (typeCount[field.type] || 0) + 1;
  }

  return signers.map((signer, i) => {
    const types = perSigner[i] || {};
    const total = Object.values(types).reduce((sum, n) => sum + n, 0);
    const breakdown = Object.entries(types)
      .map(([type, count]) => `${count} ${type.replace("_", " ")}${count > 1 ? "s" : ""}`)
      .join(", ");
    return {
      name: signer.name || `Signer ${i + 1}`,
      color: signer.color,
      total,
      breakdown,
    };
  });
}

// ─── Section Component ────────────────────────────────────────────────────────

const Section: React.FC<{
  icon: React.FC<any>;
  title: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => (
  <div className="bg-slate-50 rounded-xl p-4 mb-4 last:mb-0">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-slate-500" />
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
    </div>
    {children}
  </div>
);

// ─── Field type icon helper ──────────────────────────────────────────────────

const FIELD_TYPE_ICON: Record<string, React.FC<any>> = {
  signature: PenTool,
  initials: Type,
  date_signed: Calendar,
  text: Type,
  checkbox: CheckSquare,
};

const FIELD_TYPE_LABEL: Record<string, string> = {
  signature: "Signature",
  initials: "Initials",
  date_signed: "Date Signed",
  text: "Text",
  checkbox: "Checkbox",
};

const SENDER_COLOR = "#374151";

// ─── Component ────────────────────────────────────────────────────────────────

const ReviewStep: React.FC<ReviewStepProps> = ({ data, onChange }) => {
  const fieldSummary = getFieldSummary(data.fields, data.signers);
  const totalFields = data.fields.length;

  // Sender fields (signerIndex === -1)
  const senderFields = data.fields
    .map((f, idx) => ({ ...f, fieldIndex: idx }))
    .filter((f) => f.signerIndex === -1);
  const hasSenderFields = senderFields.length > 0;

  // Local state for signature mode per field
  const [sigModes, setSigModes] = useState<Record<number, "draw" | "type">>({});

  const getSignatureMode = (fieldIndex: number) => sigModes[fieldIndex] || "draw";
  const setSignatureMode = (fieldIndex: number, mode: "draw" | "type") =>
    setSigModes((prev) => ({ ...prev, [fieldIndex]: mode }));

  const senderValues = data.senderFieldValues || {};

  // Auto-fill date fields with today on first render
  React.useEffect(() => {
    if (!onChange || !hasSenderFields) return;
    const today = new Date().toISOString().slice(0, 10);
    let needsUpdate = false;
    const updated = { ...senderValues };
    for (const f of senderFields) {
      if (f.type === "date_signed" && !senderValues[f.fieldIndex]) {
        updated[f.fieldIndex] = today;
        needsUpdate = true;
      }
    }
    if (needsUpdate) {
      onChange({ ...data, senderFieldValues: updated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSenderValue = (fieldIndex: number, value: string) => {
    if (!onChange) return;
    const updated = { ...data, senderFieldValues: { ...senderValues, [fieldIndex]: value } };
    onChange(updated);
  };

  return (
    <div>
      {/* Matter & Subject */}
      <Section icon={FileText} title="Details">
        <div className="space-y-2">
          {data.matterId && (
            <div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Matter
              </span>
              <p className="text-sm text-slate-700 mt-0.5">
                {data.matterId}
              </p>
            </div>
          )}
          <div>
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Subject
            </span>
            <p className="text-sm font-semibold text-slate-800 mt-0.5">
              {data.subject || "No subject"}
            </p>
          </div>
          {data.message && (
            <div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Message
              </span>
              <div className="mt-1 pl-3 border-l-2 border-slate-300">
                <p className="text-sm text-slate-600 italic">{data.message}</p>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents">
        {data.documents.filter((d) => d.documentId).length === 0 ? (
          <p className="text-sm text-slate-400">No documents added</p>
        ) : (
          <div className="space-y-2">
            {data.documents
              .filter((d) => d.documentId)
              .map((doc, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-700">
                      {doc.title || doc.documentId}
                    </span>
                  </div>
                  {doc.requiresSignature && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium">
                      <CheckCircle className="h-3 w-3" />
                      Signature required
                    </span>
                  )}
                </div>
              ))}
          </div>
        )}
      </Section>

      {/* Signers */}
      <Section icon={Users} title="Signers">
        {data.signers.filter((s) => s.name || s.email).length === 0 ? (
          <p className="text-sm text-slate-400">No signers added</p>
        ) : (
          <div className="space-y-2">
            {/* Sequential flow visualization */}
            {data.sequentialSigning && data.signers.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap mb-3 pb-3 border-b border-slate-200">
                {data.signers
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((signer, i, arr) => (
                    <React.Fragment key={i}>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-white"
                        style={{ backgroundColor: signer.color }}
                      >
                        {signer.name || `Signer ${signer.order}`}
                      </span>
                      {i < arr.length - 1 && (
                        <ArrowRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
              </div>
            )}

            {data.signers.map((signer, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white rounded-lg px-3 py-2.5 border border-slate-200"
              >
                <div
                  className="h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: signer.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {signer.name || "Unnamed"}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {signer.email || "No email"}
                  </p>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium">
                  {formatRole(signer.role)}
                </span>
                <span className="text-xs text-slate-400 font-mono">
                  #{signer.order}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Placed Fields */}
      <Section icon={MousePointer} title="Placed Fields">
        {totalFields === 0 ? (
          <p className="text-sm text-slate-400">
            No fields placed yet. You can place fields from the detail
            page after creation.
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600 mb-2">
              <span className="font-semibold">{totalFields}</span> total field
              {totalFields !== 1 ? "s" : ""} placed
            </p>
            {/* Sender fields summary */}
            {senderFields.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <UserCircle className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                <span className="font-medium">Me (Sender):</span>
                <span>
                  {senderFields.length} field{senderFields.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
            {fieldSummary.map((summary, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-slate-600"
              >
                <div
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: data.signers[i]?.color || "#94a3b8" }}
                />
                <span className="font-medium">{summary.name}:</span>
                <span>
                  {summary.total} field{summary.total !== 1 ? "s" : ""}
                  {summary.breakdown ? ` (${summary.breakdown})` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Sender Fields - Fill Your Fields */}
      {hasSenderFields && (
        <Section icon={UserCircle} title="Your Fields (Sender)">
          <p className="text-sm text-slate-500 mb-3">
            Fill in your fields before sending. These will appear pre-filled on the document.
          </p>
          <div className="space-y-4">
            {senderFields.map((field) => {
              const FieldIcon = FIELD_TYPE_ICON[field.type] || Type;
              const docTitle = data.documents[field.documentIndex]?.title || `Document ${field.documentIndex + 1}`;

              return (
                <div
                  key={field.fieldIndex}
                  className="bg-white rounded-lg p-3 border border-slate-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: SENDER_COLOR }}
                    />
                    <FieldIcon className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs font-medium text-slate-600">
                      {field.label || FIELD_TYPE_LABEL[field.type]}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto">
                      {docTitle}, p.{field.page}
                    </span>
                  </div>

                  {/* Text input */}
                  {field.type === "text" && (
                    <input
                      type="text"
                      value={senderValues[field.fieldIndex] || ""}
                      onChange={(e) => updateSenderValue(field.fieldIndex, e.target.value)}
                      placeholder={field.label || "Enter text"}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                    />
                  )}

                  {/* Date input */}
                  {field.type === "date_signed" && (
                    <input
                      type="date"
                      value={senderValues[field.fieldIndex] || new Date().toISOString().slice(0, 10)}
                      onChange={(e) => updateSenderValue(field.fieldIndex, e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                    />
                  )}

                  {/* Checkbox */}
                  {field.type === "checkbox" && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={senderValues[field.fieldIndex] === "true"}
                        onChange={(e) =>
                          updateSenderValue(field.fieldIndex, e.target.checked ? "true" : "false")
                        }
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-slate-600">
                        {field.label || "Check"}
                      </span>
                    </label>
                  )}

                  {/* Signature / Initials */}
                  {(field.type === "signature" || field.type === "initials") && (
                    <div>
                      {/* Draw / Type toggle */}
                      <div className="flex gap-1 mb-2">
                        <button
                          type="button"
                          onClick={() => setSignatureMode(field.fieldIndex, "draw")}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            getSignatureMode(field.fieldIndex) === "draw"
                              ? "bg-slate-800 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <PenTool className="h-3 w-3" />
                          Draw
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignatureMode(field.fieldIndex, "type")}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            getSignatureMode(field.fieldIndex) === "type"
                              ? "bg-slate-800 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <Type className="h-3 w-3" />
                          Type
                        </button>
                      </div>

                      {getSignatureMode(field.fieldIndex) === "draw" ? (
                        <SignatureCanvas
                          onChange={(dataUrl) =>
                            updateSenderValue(field.fieldIndex, dataUrl || "")
                          }
                          height={field.type === "initials" ? 120 : 160}
                        />
                      ) : (
                        <SignatureTyped
                          onChange={(dataUrl, _text) =>
                            updateSenderValue(field.fieldIndex, dataUrl || "")
                          }
                        />
                      )}

                      {senderValues[field.fieldIndex] && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {field.type === "signature" ? "Signature" : "Initials"} captured
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Options */}
      <Section icon={Settings} title="Options">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
            <p className="text-xs text-slate-400 font-medium">Sequential Signing</p>
            <p className="text-sm font-medium text-slate-700 mt-0.5">
              {data.sequentialSigning ? "Yes" : "No"}
            </p>
          </div>
          <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-slate-400" />
              <p className="text-xs text-slate-400 font-medium">Expiration</p>
            </div>
            <p className="text-sm font-medium text-slate-700 mt-0.5">
              {data.expirationDays} days
            </p>
          </div>
          <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
            <div className="flex items-center gap-1">
              <Bell className="h-3 w-3 text-slate-400" />
              <p className="text-xs text-slate-400 font-medium">Reminders</p>
            </div>
            <p className="text-sm font-medium text-slate-700 mt-0.5">
              {data.reminders.enabled
                ? `Every ${data.reminders.intervalDays} days (max ${data.reminders.maxReminders})`
                : "Disabled"}
            </p>
          </div>
          {data.saveAsTemplate && (
            <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
              <div className="flex items-center gap-1">
                <Copy className="h-3 w-3 text-slate-400" />
                <p className="text-xs text-slate-400 font-medium">Save as Template</p>
              </div>
              <p className="text-sm font-medium text-slate-700 mt-0.5 truncate">
                {data.templateName || "Unnamed template"}
              </p>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

export default ReviewStep;
